import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/api";
import { PageHeader } from "@/components/Shared";
import { DynamicFields } from "@/components/DynamicFields";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Settings as SettingsIcon, Plus, Building2, CalendarRange, Trash2 } from "lucide-react";

const CO_EMPTY = { company_name: "", gstin: "", address: "", state: "", phone: "", email: "", helpline: "", invoice_prefix: "GE", invoice_footer: "", custom: {} };
const selCls = "bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900";
const errText = (err, fb) => { const d = err.response?.data?.detail; return typeof d === "string" ? d : fb; };

export default function SettingsPage() {
  const [form, setForm] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [fys, setFys] = useState([]);
  const [coOpen, setCoOpen] = useState(false);
  const [coEditing, setCoEditing] = useState(null);
  const [coForm, setCoForm] = useState(CO_EMPTY);
  const [fyLabel, setFyLabel] = useState("");
  const [fyDates, setFyDates] = useState({ start: "", end: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/settings").then((r) => setForm({ ...CO_EMPTY, ...r.data })).catch(() => {});
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
    api.get("/fy").then((r) => setFys(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  if (!form)
    return <div className="py-20 text-center font-mono2 text-sm text-emerald-600 animate-pulse">Loading…</div>;

  const saveSettings = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.put("/settings", form);
      setForm({ ...CO_EMPTY, ...r.data });
      toast.success("Company profile saved — invoices & PDFs will use these details");
    } catch (err) { toast.error(errText(err, "Save failed")); }
    finally { setBusy(false); }
  };

  const openNewCo = () => { setCoEditing(null); setCoForm(CO_EMPTY); setCoOpen(true); };
  const openEditCo = (c) => { setCoEditing(c.company_id); setCoForm({ ...CO_EMPTY, ...c }); setCoOpen(true); };

  const saveCompany = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (coEditing) {
        await api.put(`/companies/${coEditing}`, coForm);
        toast.success("Company updated");
      } else {
        await api.post("/companies", coForm);
        toast.success("New company created — activate it to print its details on invoices");
      }
      setCoOpen(false); load();
    } catch (err) { toast.error(errText(err, "Save failed")); }
    finally { setBusy(false); }
  };

  const activateCompany = async (c) => {
    try {
      await api.post(`/companies/${c.company_id}/activate`);
      toast.success(`${c.company_name} is now the active company`);
      load();
    } catch (err) { toast.error(errText(err, "Activate failed")); }
  };

  const deleteCompany = async (c) => {
    if (!window.confirm(`Delete company ${c.company_name}?`)) return;
    try {
      await api.delete(`/companies/${c.company_id}`);
      toast.success("Company deleted");
      load();
    } catch (err) { toast.error(errText(err, "Delete failed")); }
  };

  const addFy = async (e) => {
    e.preventDefault();
    if (!fyLabel.trim()) return;
    try {
      await api.post("/fy", {
        label: fyLabel.trim(),
        start_date: fyDates.start || undefined,
        end_date: fyDates.end || undefined,
      });
      toast.success(`Financial year ${fyLabel} created`);
      setFyLabel(""); setFyDates({ start: "", end: "" }); load();
    } catch (err) { toast.error(errText(err, "Create failed")); }
  };

  const activateFy = async (f) => {
    try {
      await api.post(`/fy/${f.fy_id}/activate`);
      toast.success(`Accounting year changed to ${f.label}`);
      load();
    } catch (err) { toast.error(errText(err, "Activate failed")); }
  };

  const deleteFy = async (f) => {
    if (!window.confirm(`Delete ${f.label}?`)) return;
    try {
      await api.delete(`/fy/${f.fy_id}`);
      toast.success("Financial year removed");
      load();
    } catch (err) { toast.error(errText(err, "Delete failed")); }
  };

  const field = (label, key, testid) => (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">{label}</Label>
      <Input value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} data-testid={testid} className={selCls} />
    </div>
  );

  return (
    <div data-testid="settings-page" className="max-w-4xl">
      <PageHeader title="Company Settings" sub="Active company profile prints on every invoice — manage companies, financial years and custom fields here" />

      {/* Active company profile */}
      <form onSubmit={saveSettings} className="rounded-2xl border border-slate-200 bg-white p-6 space-y-5 mb-6" data-testid="company-profile-form">
        <div className="flex items-center gap-4 pb-4 border-b border-slate-200">
          <span className="bg-white rounded-xl px-3 py-1.5 inline-flex">
            <img src="/logo.png" alt="GreeErgy" className="h-9" data-testid="settings-logo" />
          </span>
          <div className="flex items-center gap-2 text-slate-600 text-sm">
            <SettingsIcon className="w-4 h-4 text-emerald-600" /> Active company · invoice identity
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {field("Company Name", "company_name", "settings-company-input")}
          {field("GSTIN", "gstin", "settings-gstin-input")}
          {field("Phone", "phone", "settings-phone-input")}
          {field("Helpline No", "helpline", "settings-helpline-input")}
          {field("Email", "email", "settings-email-input")}
          {field("State", "state", "settings-state-input")}
          {field("Invoice Prefix", "invoice_prefix", "settings-prefix-input")}
        </div>
        {field("Registered Address", "address", "settings-address-input")}
        {field("Invoice Footer / Terms", "invoice_footer", "settings-footer-input")}
        <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/5 p-4">
          <p className="text-xs font-mono2 uppercase tracking-[0.18em] text-emerald-600 mb-3">Admin Custom Fields (company)</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <DynamicFields entity="company" values={form.custom || {}} onChange={(k, v) => setForm({ ...form, custom: { ...(form.custom || {}), [k]: v } })} />
          </div>
        </div>
        <Button type="submit" disabled={busy} data-testid="settings-save-btn"
          className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold rounded-full px-8 transition-colors duration-200">
          {busy ? "Saving…" : "Save Settings"}
        </Button>
      </form>

      {/* Companies */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 mb-6" data-testid="companies-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-emerald-600" />
            <h3 className="font-semibold text-slate-900">Companies</h3>
          </div>
          <Button onClick={openNewCo} data-testid="new-company-btn" size="sm"
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold rounded-full transition-colors duration-200">
            <Plus className="w-4 h-4 mr-1" /> New Company
          </Button>
        </div>
        <div className="space-y-2">
          {companies.map((c) => (
            <div key={c.company_id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-slate-100 px-4 py-3" data-testid={`company-row-${c.company_id}`}>
              <div>
                <span className="text-sm font-medium text-slate-900">{c.company_name}</span>
                {c.gstin && <span className="ml-2 text-xs font-mono2 text-emerald-600">{c.gstin}</span>}
                {c.active && <span className="ml-2 px-2 py-0.5 rounded-full border border-emerald-500/40 text-emerald-600 bg-emerald-500/10 text-[10px] font-mono2 uppercase">Active</span>}
              </div>
              <div className="flex items-center gap-1">
                {!c.active && (
                  <button onClick={() => activateCompany(c)} data-testid={`company-activate-${c.company_id}`}
                    className="text-xs text-emerald-600 hover:text-emerald-700 px-2 py-1 rounded-lg hover:bg-emerald-500/10 transition-colors duration-150">
                    Set Active
                  </button>
                )}
                <button onClick={() => openEditCo(c)} data-testid={`company-edit-${c.company_id}`}
                  className="text-xs text-slate-700 hover:text-emerald-700 px-2 py-1 rounded-lg hover:bg-slate-700/50 transition-colors duration-150">Edit</button>
                {!c.active && (
                  <button onClick={() => deleteCompany(c)} data-testid={`company-delete-${c.company_id}`}
                    className="text-xs text-red-600 hover:text-red-700 px-2 py-1 rounded-lg hover:bg-red-500/10 transition-colors duration-150">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Financial years */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="fy-card">
        <div className="flex items-center gap-2 mb-4">
          <CalendarRange className="w-4 h-4 text-emerald-600" />
          <h3 className="font-semibold text-slate-900">Financial Years</h3>
        </div>
        <form onSubmit={addFy} className="flex flex-wrap gap-2 mb-4">
          <Input value={fyLabel} onChange={(e) => setFyLabel(e.target.value)} placeholder="e.g. FY 2027-28" data-testid="fy-label-input" className={`${selCls} max-w-xs`} />
          <Input type="date" value={fyDates.start} onChange={(e) => setFyDates({ ...fyDates, start: e.target.value })} title="FY start date (optional)" data-testid="fy-start-input" className={`${selCls} max-w-[160px]`} />
          <Input type="date" value={fyDates.end} onChange={(e) => setFyDates({ ...fyDates, end: e.target.value })} title="FY end date (optional)" data-testid="fy-end-input" className={`${selCls} max-w-[160px]`} />
          <Button type="submit" data-testid="fy-create-btn"
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold rounded-full transition-colors duration-200">
            <Plus className="w-4 h-4 mr-1" /> Create FY
          </Button>
        </form>
        <p className="text-[11px] text-slate-500 mb-3">Start/end dates are optional — if left blank they are derived from the label (Indian FY: Apr–Mar). Backdated invoices must fall inside the active FY.</p>
        <div className="space-y-2">
          {fys.map((f) => (
            <div key={f.fy_id} className="flex items-center justify-between rounded-xl bg-slate-100 px-4 py-2.5" data-testid={`fy-row-${f.fy_id}`}>
              <div>
                <span className="text-sm font-medium text-slate-900 font-mono2">{f.label}</span>
                {f.start_date && f.end_date && (
                  <span className="ml-2 text-[11px] text-slate-500 font-mono2">{f.start_date} → {f.end_date}</span>
                )}
                {f.active && <span className="ml-2 px-2 py-0.5 rounded-full border border-emerald-500/40 text-emerald-600 bg-emerald-500/10 text-[10px] font-mono2 uppercase">Current</span>}
              </div>
              <div className="flex items-center gap-1">
                {!f.active && (
                  <>
                    <button onClick={() => activateFy(f)} data-testid={`fy-activate-${f.fy_id}`}
                      className="text-xs text-emerald-600 hover:text-emerald-700 px-2 py-1 rounded-lg hover:bg-emerald-500/10 transition-colors duration-150">Change to this FY</button>
                    <button onClick={() => deleteFy(f)} data-testid={`fy-delete-${f.fy_id}`}
                      className="text-xs text-red-600 hover:text-red-700 px-2 py-1 rounded-lg hover:bg-red-500/10 transition-colors duration-150">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Company create/edit dialog */}
      <Dialog open={coOpen} onOpenChange={setCoOpen}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-lg" data-testid="company-dialog">
          <DialogHeader><DialogTitle>{coEditing ? "Edit Company" : "Create New Company"}</DialogTitle></DialogHeader>
          <form onSubmit={saveCompany} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
            {[["Company Name *", "company_name", true], ["GSTIN", "gstin", false], ["Phone", "phone", false],
              ["Helpline No", "helpline", false], ["Email", "email", false], ["State", "state", false],
              ["Invoice Prefix", "invoice_prefix", false]].map(([l, k, req]) => (
              <div key={k} className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">{l}</Label>
                <Input required={req} value={coForm[k] || ""} onChange={(e) => setCoForm({ ...coForm, [k]: e.target.value })} data-testid={`company-${k}-input`} className={selCls} />
              </div>
            ))}
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Address</Label>
              <Input value={coForm.address || ""} onChange={(e) => setCoForm({ ...coForm, address: e.target.value })} data-testid="company-address-input" className={selCls} />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Invoice Footer / Terms</Label>
              <Input value={coForm.invoice_footer || ""} onChange={(e) => setCoForm({ ...coForm, invoice_footer: e.target.value })} data-testid="company-footer-input" className={selCls} />
            </div>
            <div className="col-span-2 pt-2">
              <Button type="submit" disabled={busy} data-testid="company-save-btn"
                className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold transition-colors duration-200">
                {busy ? "Saving…" : coEditing ? "Update Company" : "Create Company"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
